import { useUserStore } from 'shared/state/user';
import { PetPurchase, Role, StripeAccountStatus } from 'shared/@types';
import { useFormatter } from 'shared/library/use-formatter';
import { useTranslation } from 'react-i18next';
import { usePartners } from 'shared/library/partners';
import { useRoute } from 'shared/library/routes/route';

export type PetPayStep = {
  isEnabled: boolean;
  completedAt: string | true | null;
  completedDescription: string;
  title: string;
  description: string;
  actionTitle?: string;
  actionHref?: string;
  actionRole?: Role;
};

export const usePetpaySteps = (petPurchase: PetPurchase) => {
  const { t } = useTranslation('library');
  const route = useRoute();
  const { formatCurrency, formatDate } = useFormatter();
  const user = useUserStore((state) => state.user);
  const role: Role = petPurchase.buyer.id === user?.id ? 'buyer' : 'seller';
  const { insurancePartner } = usePartners();

  const steps: PetPayStep[] = [
    {
      isEnabled: true,
      title: t('use_petpay_steps.reservation'),
      description: '',
      completedAt: petPurchase.created_at!,
      completedDescription: t('use_petpay_steps.reservation_completed_description', {
        buyer: petPurchase.buyer.display_name,
        pet: petPurchase.pet.name,
      }),
    },
    {
      isEnabled: true,
      title: t('use_petpay_steps.account_verification'),
      description: t('use_petpay_steps.account_verification_description'),
      completedAt: petPurchase.buyer.verified_at!,
      completedDescription: t('use_petpay_steps.account_verification_completed_description'),
      actionTitle: t('use_petpay_steps.account_verification_action'),
      actionHref: route('me.verification'),
      actionRole: 'buyer' as const,
    },
    {
      isEnabled: petPurchase.deposit > 0,
      title: t('use_petpay_steps.deposit_payment'),
      description: t('use_petpay_steps.deposit_payment_description'),
      completedAt: petPurchase.deposit_paid_at,
      completedDescription: t('use_petpay_steps.deposit_payment_completed_description', {
        deposit: formatCurrency(petPurchase.deposit, petPurchase.currency),
      }),
      actionTitle: t('use_petpay_steps.deposit_payment_action'),
      actionHref: route('petpay.deposit', { petPurchase: petPurchase.uuid }),
      actionRole: 'buyer' as Role,
    },
    {
      isEnabled: role === 'seller',
      title: t('use_petpay_steps.payout_details'),
      description: t('use_petpay_steps.payout_details_description'),
      completedAt:
        user?.stripe_account_status === StripeAccountStatus.Active ? (true as const) : null,
      completedDescription: t('use_petpay_steps.payout_details_completed_description'),
      actionTitle: t('use_petpay_steps.payout_details_action'),
      actionHref: route('me.payout-details'),
    },
    {
      isEnabled: role === 'buyer',
      title: t('use_petpay_steps.pet_insurance'),
      description: t('use_petpay_steps.pet_insurance_description'),
      completedAt: petPurchase.booked_insurance_call !== null ? petPurchase.updated_at : null,
      completedDescription: petPurchase.booked_insurance_call
        ? t('use_petpay_steps.pet_insurance_completed_description', {
            partner: insurancePartner.name,
            bookedAt: formatDate(petPurchase.insurance_call?.scheduled_at),
          })
        : t('use_petpay_steps.pet_insurance_declined_description', {
            partner: insurancePartner.name,
          }),
      actionTitle: t('use_petpay_steps.pet_insurance_action'),
      actionHref: route('petpay.insurance', { petPurchase: petPurchase.uuid }),
    },
    {
      isEnabled: true,
      title: t('use_petpay_steps.purchase_agreement'),
      description: t('use_petpay_steps.purchase_agreement_description'),
      completedAt: petPurchase.contract.signed_at ?? petPurchase.contract.deleted_at ?? null,
      completedDescription: petPurchase.contract.deleted_at
        ? t('use_petpay_steps.purchase_agreement_deleted_description')
        : t('use_petpay_steps.purchase_agreement_completed_description'),
      actionTitle: t('use_petpay_steps.purchase_agreement_action'),
      actionHref:
        role === 'seller'
          ? route('petpay.contract.edit', { petPurchase: petPurchase.uuid })
          : route('petpay.contract.sign', { petPurchase: petPurchase.uuid }),
      actionRole: (petPurchase.contract.seller_signed_at ? 'buyer' : 'seller') as Role,
    },
    {
      isEnabled: true,
      title: t('use_petpay_steps.final_payment'),
      description: t('use_petpay_steps.final_payment_description'),
      completedAt: petPurchase.final_payment_paid_at,
      completedDescription: t('use_petpay_steps.final_payment_completed_description', {
        finalPayment: formatCurrency(
          petPurchase.price - (petPurchase.deposit ?? 0),
          petPurchase.currency,
        ),
      }),
      actionTitle: t('use_petpay_steps.final_payment_action'),
      actionHref: route('petpay.final-payment', { petPurchase: petPurchase.uuid }),
      actionRole: 'buyer' as Role,
    },
    {
      isEnabled: true,
      title: t('use_petpay_steps.pickup'),
      description: t('use_petpay_steps.pickup_description'),
      completedAt: petPurchase.delivery_confirmed_at,
      completedDescription: t('use_petpay_steps.pickup_completed_description', {
        pet: petPurchase.pet.name,
      }),
      actionTitle: t('use_petpay_steps.pickup_action'),
      actionHref: route('petpay.pickup', { petPurchase: petPurchase.uuid }),
      actionRole: 'buyer' as Role,
    },
  ].filter((step) => step.isEnabled);

  const currentStepIndex = steps.findIndex((step) => !step.completedAt);

  const currentStep = steps[currentStepIndex];

  return { steps, currentStepIndex, currentStep, role };
};
